import React, { useEffect, useRef } from 'react';
import { Camera, Film, Image as ImageIcon, Drone, Palette, User } from 'lucide-react';
import { Helmet } from "react-helmet-async";
const services = [
    { id: '01', icon: Camera, title: 'Creative Theme Planning', desc: 'Unique concepts tailored to your relationship and style.' },
    { id: '02', icon: ImageIcon, title: 'Outdoor Couple Shoots', desc: 'Beautiful locations including beaches, gardens, and forts.' },
    { id: '03', icon: ImageIcon, title: 'Destination Pre Wedding', desc: 'Capture breathtaking memories at stunning locations across India.' },
    { id: '04', icon: Drone, title: 'Drone Photography', desc: 'Spectacular aerial shots that add grandeur to your memories.' },
    { id: '05', icon: Palette, title: 'Costume & Style Guidance', desc: 'Expert suggestions for outfits, colors, and styling.' },
    { id: '06', icon: Film, title: 'Cinematic Films', desc: 'Romantic short films with storytelling and cinematic editing.' },
];

const weddingServices = [
    { id: '01', icon: Camera, title: 'Traditional Photography', desc: 'Beautifully documented rituals and family moments.' },
    { id: '02', icon: ImageIcon, title: 'Candid Photography', desc: 'Natural emotions captured without posing.' },
    { id: '03', icon: Film, title: 'Wedding Cinematography', desc: 'Movie-style wedding films with cinematic storytelling.' },
    { id: '04', icon: User, title: 'Bridal Portraits', desc: 'Elegant portraits highlighting every detail.' },
    { id: '05', icon: User, title: 'Groom Portraits', desc: 'Stylish portraits that celebrate the groom\'s personality.' },
];

const wedding = [
    { id: '01', icon: Camera, title: 'Engagement Photography', desc: 'Capturing the beginning of your forever story.' },
    { id: '02', icon: Palette, title: 'Haldi Ceremony Photography', desc: 'Vibrant colors and joyful traditions.' },
    { id: '03', icon: Camera, title: 'Mehendi Photography', desc: 'Intricate details and artistic celebrations.' },
    { id: '04', icon: Camera, title: 'Sangeet Photography', desc: 'Music, dance, and energetic performances.' },
    { id: '05', icon: Camera, title: 'Reception Photography', desc: 'Elegant portraits and grand celebrations.' },
    { id: '06', icon: User, title: 'Family & Group Portraits', desc: 'Cherished memories with your loved ones.' },
    { id: '07', icon: Drone, title: 'Drone Wedding Coverage', desc: 'Spectacular aerial perspectives of your venue.' },
    { id: '08', icon: Film, title: 'Highlight Wedding Films', desc: 'Curated storytelling films of your big day.' },
    { id: '09', icon: ImageIcon, title: 'Luxury Wedding Albums', desc: 'Premium print services for a lifetime keepsake.' },
];

const PreWeddingandWedding = () => {
    // 1. Create mutable references for the DOM elements
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        // 2. Fetch the current underlying elements safely from the refs
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        // Safety Guard Clause: If elements aren't mounted yet, stop early.
        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.6;
        const speedRight = 0.4;

        // Hover handlers
        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        // Animation Loop Logic
        function performAutoScroll() {
            // Check existence inside the loop dynamically to prevent async teardown crashes
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        // Initialize the animation frame loop
        animationFrameId = requestAnimationFrame(performAutoScroll);

        // 3. CLEANUP: Vital step to clear loops and events when components change or unmount
        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []); // Empty array ensures this setup loop executes exactly once on component load

    return (
        <div>
            <Helmet>
                <title>Wedding and Pre Wedding Photography in Hyderabad | KhultStudios</title>

                <meta
                    name="description"
                    content="Capture your love story with KhultStudios. We offer professional Wedding Photography, Pre Wedding Shoots, Candid Photography, Traditional Photography, Cinematic Wedding Films, Drone Coverage, and Engagement Photography in Hyderabad."
                />

                <meta
                    name="keywords"
                    content="Wedding Photography Hyderabad, Pre Wedding Photography Hyderabad, Pre Wedding Shoot Hyderabad, Candid Wedding Photography, Traditional Wedding Photography, Bridal Photography, Groom Photography, Engagement Photography, Wedding Videography, Cinematic Wedding Films, Drone Wedding Photography, Best Wedding Photographer Hyderabad, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/wedding-pre-wedding-photography"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/wedding-pre-wedding-photography"
                />
                <meta
                    property="og:title"
                    content="Wedding & Pre Wedding Photography in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Professional Wedding & Pre Wedding Photography in Hyderabad with candid moments, cinematic wedding films, drone coverage, engagement shoots, and timeless storytelling."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/wedding-photography-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Wedding & Pre Wedding Photography | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Book professional Wedding & Pre Wedding Photography in Hyderabad. Candid photography, cinematic films, engagement shoots, and drone coverage."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/wedding-photography-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/khultstudio.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Pre Wedding and Wedding Photography Services
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Creating Timeless Love Stories with KhultStudio
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Your wedding is one of the most memorable milestones of your life, and every emotion deserves to be preserved beautifully. At KhultStudio, we specialize in capturing authentic love stories through creative photography and cinematic filmmaking. From romantic pre-wedding shoots to every joyful moment of your wedding celebrations, our experienced photographers ensure that every memory lasts forever.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        With a passion for storytelling, advanced photography equipment, and artistic vision, we transform precious moments into timeless memories that you and your family will cherish for generations.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        See More
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-tiktok" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    {/* Linked via ref hooks */}
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?q=80&w=500" alt="Wedding 1" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=500" alt="Wedding 2" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=500" alt="Wedding 3" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1511285560929-80b456fea0bc?q=80&w=500" alt="Wedding 4" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?q=80&w=500" alt="Wedding 1 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?q=80&w=500" alt="Wedding 2 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=500" alt="Wedding 3 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1511285560929-80b456fea0bc?q=80&w=500" alt="Wedding 4 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/pre-wedding-and-wedding-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/events-and-celebrations-photography.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/Arts.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/modelling-and-acting-photography.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/home/movie-posters.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/pre-wedding-and-wedding-photography.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/products-and-brands-photography.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/maternity-and-baby-photography1.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>



            <section className="services-section py-5">
                <div className="container">
                    <h2 className="text-center mb-5 text-white"> 💍 Pre Wedding Photography Service</h2>
                    <p className="text-light-gray mt-3">
                        A pre-wedding shoot is more than just a photoshoot—it's the beginning of your forever story. Our creative team designs personalized concepts that reflect your personalities, love story, and dreams.
                    </p>
                    <div className="row g-4">
                        {services.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 1 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>



            <section className="wedding-services-section py-5">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="text-white">👰 Wedding Photography Service</h2>
                        <p className="text-light-gray mt-3">
                            Your wedding is filled with priceless emotions, traditions, laughter, and unforgettable moments.
                            Our photographers document every celebration with creativity and attention to detail.
                        </p>
                    </div>

                    <div className="row g-4">
                        {weddingServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>


            <div className="row g-4">
                {wedding.map((service, index) => (
                    <div key={index} className="col-lg-4 col-md-6"> {/* 3 per row on LG, 2 on MD */}
                        <div className="service-card">
                            <div className="card-content">
                                <service.icon className="icon mb-3" size={40} />
                                <h4>{service.title}</h4>
                                <p>{service.desc}</p>
                            </div>
                            <span className="card-number">{service.id}</span>
                        </div>
                    </div>
                ))}
            </div>


        </div>
    );
};

export default PreWeddingandWedding;



